import { useState } from 'react'
import { useGetAdminCategoriesQuery, useCreateCategoryMutation, useUpdateCategoryMutation, useDeleteCategoryMutation } from '../../store/api/agentsApi'
import type { CategoryAdminRecord } from '../../store/api/agentsApi'
import { getErrorMessage } from '../../lib/apiClient'
import { Button, Alert, StatusPill, Modal, AgentFormField } from '../../components/ui'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import { EMOJI_ICON_MAX_LEN } from '../../lib/config'
import s from '../../styles/pages/AdminPage.module.css'

export function AdminCategories() {
  const { data, isLoading, isFetching, refetch } = useGetAdminCategoriesQuery()
  const [createCategory] = useCreateCategoryMutation()
  const [updateCategory] = useUpdateCategoryMutation()
  const [deleteCategory] = useDeleteCategoryMutation()
  const categories = data?.categories ?? []

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<CategoryAdminRecord | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const BLANK = { name: '', slug: '', description: '', color: '', icon: '', isActive: true, sortOrder: 0 }
  const [form, setForm] = useState(BLANK)

  const openCreate = () => { setEditing(null); setForm(BLANK); setError(''); setShowForm(true) }
  const openEdit = (c: CategoryAdminRecord) => {
    setEditing(c)
    setForm({ name: c.name, slug: c.slug, description: c.description ?? '', color: c.color ?? '', icon: c.icon ?? '', isActive: c.isActive, sortOrder: c.sortOrder })
    setError(''); setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null); setError('') }

  const handleNameChange = (name: string) => {
    const slug = editing ? form.slug : name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    setForm({ ...form, name, slug })
  }

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return }
    if (!form.slug.trim()) { setError('Slug is required'); return }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug.trim())) { setError('Slug must be lowercase letters, numbers and hyphens only'); return }
    setSaving(true); setError('')
    try {
      const body = { ...form, color: form.color.trim() || null, icon: form.icon.trim() || null, description: form.description.trim() || undefined }
      if (editing) await updateCategory({ id: editing.id, body }).unwrap()
      else await createCategory(body).unwrap()
      closeForm()
    } catch (err) { setError(getErrorMessage(err, 'Save failed. Please try again.')) }
    setSaving(false)
  }

  const handleDeactivate = async (id: string, name: string) => {
    if (!window.confirm(`Deactivate category "${name}"?`)) return
    await deleteCategory(id).unwrap().catch(() => {})
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Categories {!isLoading && `(${categories.length})`}</div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
          <Button size="sm" onClick={openCreate}>+ New Category</Button>
        </div>
      </div>

      {showForm && (
        <Modal onClose={closeForm} maxWidth="480px">
          <h3 className={s.formModalTitle}>{editing ? 'Edit Category' : 'New Category'}</h3>
          {error && <Alert variant="error" style={{ marginBottom: '1rem' }}>{error}</Alert>}
          <AgentFormField label="Name *"><input value={form.name} onChange={(e) => handleNameChange(e.target.value)} className={s.formInput} placeholder="e.g. Food & Cooking" /></AgentFormField>
          <AgentFormField label="Slug *"><input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={s.formInput} placeholder="food-cooking" /></AgentFormField>
          <div className={s.formGrid2}>
            <AgentFormField label="Icon (emoji)"><input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className={s.formInput} placeholder="🍳" maxLength={EMOJI_ICON_MAX_LEN} /></AgentFormField>
            <AgentFormField label="Color (hex)">
              <div className={s.colorInputRow}>
                <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={s.formInput} style={{ flex: 1 }} placeholder="#f97316" />
                <input type="color" value={form.color || '#f97316'} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: '2rem', height: '2rem', padding: 0, border: 'none', borderRadius: '4px', cursor: 'pointer', background: 'none' }} />
              </div>
            </AgentFormField>
          </div>
          <AgentFormField label="Description"><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={s.formInput} placeholder="Optional description" /></AgentFormField>
          <div className={s.formGrid2}>
            <AgentFormField label="Sort Order"><input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className={s.formInput} /></AgentFormField>
            <AgentFormField label="Status">
              <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })} className={s.formInput}>
                <option value="active">Active</option><option value="inactive">Inactive</option>
              </select>
            </AgentFormField>
          </div>
          <div className={s.formActions}>
            <Button variant="secondary" onClick={closeForm}>Cancel</Button>
            <Button onClick={() => void handleSave()} loading={saving}>{editing ? 'Save changes' : 'Create category'}</Button>
          </div>
        </Modal>
      )}

      <div className={s.listCard}>
        {isLoading && <div className={s.listCardLoading}>Loading categories...</div>}
        {categories.map((cat) => (
          <div key={cat.id} className={s.listRow}>
            <div className={s.catIcon} style={{ background: `${cat.color ?? '#7c3aed'}22`, border: `1px solid ${cat.color ?? '#7c3aed'}44` }}>{cat.icon ?? '💬'}</div>
            <div className={s.catInfo}>
              <div className={s.catName}>{cat.name}</div>
              <div className={s.catSlug}>/{cat.slug} · order {cat.sortOrder}</div>
            </div>
            <StatusPill variant={cat.isActive ? 'success' : 'warning'}>{cat.isActive ? 'active' : 'inactive'}</StatusPill>
            <Button size="sm" variant="secondary" onClick={() => openEdit(cat)}>✏️ Edit</Button>
            {cat.isActive && <Button size="sm" variant="danger" onClick={() => void handleDeactivate(cat.id, cat.name)}>Deactivate</Button>}
          </div>
        ))}
        {!isLoading && categories.length === 0 && <div className={s.listCardEmpty}>No categories found</div>}
      </div>
    </div>
  )
}
